import type { MediaAsset } from '@slideforge/shared';
import { extensionOf, type OpcPackage } from './opc.js';

/**
 * Media parts are content-addressed by SHA-256 so a logo placed on forty slides
 * is stored once. WebCrypto is used rather than a bundled hash implementation:
 * it is present in browsers, in Web Workers and in Node 18+, which is every
 * environment this package runs in.
 */
export interface MediaTable {
  /** Asset metadata, keyed by content hash. */
  readonly assets: ReadonlyMap<string, MediaAsset>;
  /** Bytes, keyed by content hash. */
  readonly bytes: ReadonlyMap<string, Uint8Array>;
  /** Content hash for a given package part. */
  readonly byPart: ReadonlyMap<string, string>;
}

const MEDIA_PART = /^\/ppt\/(media|embeddings)\//i;

export async function buildMediaTable(pkg: OpcPackage): Promise<MediaTable> {
  const assets = new Map<string, MediaAsset>();
  const bytes = new Map<string, Uint8Array>();
  const byPart = new Map<string, string>();

  for (const partName of pkg.partNames) {
    if (!MEDIA_PART.test(partName)) continue;
    const data = pkg.bytes(partName);
    if (!data) continue;

    const id = await sha256Hex(data);
    byPart.set(partName, id);
    if (assets.has(id)) continue;

    const extension = extensionOf(partName);
    const dimensions = imageDimensions(data, extension);
    assets.set(id, {
      id,
      partName,
      mimeType: pkg.contentType(partName),
      byteLength: data.byteLength,
      extension,
      ...(dimensions ?? {}),
    });
    bytes.set(id, data);
  }

  return { assets, bytes, byPart };
}

export async function sha256Hex(data: Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      'WebCrypto is unavailable, so media cannot be content-addressed. This package requires a browser, a Web Worker, or Node 18 or newer.',
    );
  }
  const buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  const digest = await subtle.digest('SHA-256', buffer as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Reads intrinsic dimensions from the file header. Only the formats PowerPoint
 * actually embeds are handled; anything else reports no dimensions rather than
 * guessing, and the publisher falls back to the shape's box.
 */
export function imageDimensions(
  data: Uint8Array,
  extension: string,
): { width: number; height: number } | undefined {
  switch (extension) {
    case 'png':
      return pngDimensions(data);
    case 'jpg':
    case 'jpeg':
      return jpegDimensions(data);
    case 'gif':
      return gifDimensions(data);
    default:
      return undefined;
  }
}

function pngDimensions(d: Uint8Array): { width: number; height: number } | undefined {
  if (d.length < 24) return undefined;
  if (d[0] !== 0x89 || d[1] !== 0x50 || d[2] !== 0x4e || d[3] !== 0x47) return undefined;
  const view = new DataView(d.buffer, d.byteOffset, d.byteLength);
  return { width: view.getUint32(16), height: view.getUint32(20) };
}

function gifDimensions(d: Uint8Array): { width: number; height: number } | undefined {
  if (d.length < 10) return undefined;
  if (d[0] !== 0x47 || d[1] !== 0x49 || d[2] !== 0x46) return undefined;
  const view = new DataView(d.buffer, d.byteOffset, d.byteLength);
  return { width: view.getUint16(6, true), height: view.getUint16(8, true) };
}

function jpegDimensions(d: Uint8Array): { width: number; height: number } | undefined {
  if (d.length < 4 || d[0] !== 0xff || d[1] !== 0xd8) return undefined;
  const view = new DataView(d.buffer, d.byteOffset, d.byteLength);
  let offset = 2;
  while (offset + 9 < d.length) {
    if (d[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = d[offset + 1];
    if (marker === undefined) return undefined;
    // SOF0-SOF15, excluding the DHT/JPG/DAC markers that share the range.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return { height: view.getUint16(offset + 5), width: view.getUint16(offset + 7) };
    }
    offset += 2 + view.getUint16(offset + 2);
  }
  return undefined;
}
